import PropTypes from 'prop-types';
import {formValueSelector, reduxForm} from 'redux-form';
import useLabels from 'infrastructure/js/hooks/useLabels';
import Validation from 'infrastructure/js/components/controls/controlsValidations.js';
import Dialog from 'infrastructure/js/components/Dialog/dialog';
import InputSection from 'infrastructure/js/components/Dialog/InputSection/inputSection';
import TextField from 'infrastructure/js/components/controls/TextField/textField';
import Button from 'infrastructure/js/components/controls/Button/button';
import EntitiesMultiSelect from '../../Common/Controls/EntitiesMultiSelect/entitiesMultiSelect';
import EntitiesMultiSelectWithChildren from '../../Common/Controls/EntitiesMultiSelect/entitiesMultiSelectWithChildren';
import {enumTypes, getEnumLabel, getEnumValue} from '../../../utils/enumHelper';
import {EntityPropertyTypes} from '../../../enums/entityPropertyTypes';
import {FetchEntitiesFilters} from '../../../enums/fetchEntitiesFilters';
import Combobox from 'infrastructure/js/components/controls/Combobox/combobox';
import List from 'infrastructure/js/components/List/list';
import UnitHelper, {unitTypes, UOM_DEFAULT_PRECISION} from 'infrastructure/js/utils/uomHelper';
import {useCallback, useEffect, useMemo, useState} from 'react';
import TextAreaField from 'infrastructure/js/components/controls/TextAreaField/textAreaField';
import {getAssetStatusesOptions, round} from '../../../utils/assetHelper';
import {connect} from 'react-redux';
import AssetLengthListItem from '../../Common/Controls/AddRemoveList/AddRemoveListItem/AssetLengthListItem/assetLengthListItem';
import * as dialogHelper from 'infrastructure/js/components/Dialog/dialogHelper';
import Label from 'infrastructure/js/components/Label/label';
import NameValuePairs from '../../Common/NameValuePairs/nameValuePairs';
import MaterialHelper from '../../../utils/materialHelper';
import AlertPopover, {AlertPopoverType} from 'infrastructure/js/components/popover/AlertPopover/alertPopover';

import './createSpoolFromCassettesDialog.scss';

const getStatuses = () => {
  return getAssetStatusesOptions();
}

const getInitialCassettes = (items = []) => {
  if (!items.length) {
    return [{
      asset: {
        assetsType: {
          value: getEnumValue(enumTypes.OBJECT_TYPE)('CASSETTE'),
          label: getEnumLabel(enumTypes.OBJECT_TYPE)('CASSETTE'),
        },
      },
      length: 0
    }]
  }

  return items.map((item) => {
    return {
      asset: {
        assetsType: {
          value: getEnumValue(enumTypes.OBJECT_TYPE)('CASSETTE'),
          label: getEnumLabel(enumTypes.OBJECT_TYPE)('CASSETTE'),
        },
        assetsValue: {
          value: item.id,
          label: item.businessId,
          data: item
        }
      },
      length: item.lengthLeft ? UnitHelper.serverValueToUserValue(unitTypes.LENGTH, item.lengthLeft) : 0,
    }
  });
}

const hasEmptyItems = (items = []) => {
  return items.some(item => !item?.asset?.assetsValue?.value);
}

const getComponentToRender = (props) => {
  if (!props?.data ) {
    return null;
  }
  let option = props.data;
  return <span className={option.isDisabled ? 'sub-rolls-status-title' : 'sub-rolls-status'}>{option.label}</span>
}

const createSourceCassettes = (items = []) => {
  return items.map(item => {
    return (
      {
        cassetteId: item?.asset?.assetsValue?.data?.id,
        lengthToConsume: UnitHelper.userValueToServerValue(unitTypes.LENGTH, +(item?.length) ?? 0)
      });
  });
}

const maxLength100 = Validation.maxLength(100);

const WO_ENTITIES_TYPES = [getEnumValue(enumTypes.OBJECT_TYPE)('WO')];

const WO_FETCH_CONFIG = {
  entityType: EntityPropertyTypes.WO_BUSINESS_ID,
  filter: [FetchEntitiesFilters.ACTIVE],
};

const SPOOL_FETCH_CONFIG = {
  entityType: EntityPropertyTypes.SPOOL_BUSINESS_ID,
  filter: [FetchEntitiesFilters.ACTIVE],
};

export function CreateSpoolFromCassettesDialog( props ) {

  const {
    actions,
    fetchEntitiesActions,
    sData,
    handleSubmit,
    change,
    touch,
    initialize,
    pristine = false,
    reloadParentComponent = null,
    fieldsValues = {},
  } = props;

  const dialogLabels = useLabels('mat.dialog.');
  const labels = useLabels('mat.locationPage.view.spooling.createSpoolFromCassettes.dialog.');
  const validationLabels = useLabels('mat.validation.messages.');
  const [printPdfLabels, setPrintPdfLabels] = useState(false);
  const [isNewSpool, setIsNewSpool] = useState(false);
  const [spoolingInfo, setSpoolingInfo] = useState([]);

  const footerCheckbox = {
    text: labels.get('footer.printPdfLabels'),
    action: (value) => {
      setPrintPdfLabels(value)
    }
  };

  const woFetchConfig = {
    ...WO_FETCH_CONFIG,
    action: fetchEntitiesActions.fetchEntities,
  };

  const spoolFetchConfig = {
    ...SPOOL_FETCH_CONFIG,
    action: fetchEntitiesActions.fetchEntities,
  };

  const cassetteFetchConfig = {
    entityType: EntityPropertyTypes.CASSETTE_BUSINESS_ID,
    filter: [FetchEntitiesFilters.ACTIVE],
    action: fetchEntitiesActions.fetchEntities,
  };

  const firstCassetteWidth = fieldsValues?.cassettesList?.[0]?.asset?.assetsValue?.data?.widthInCentimeter;
  const firstCassetteMaterialId = fieldsValues?.cassettesList?.[0]?.asset?.assetsValue?.data?.material?.id;

  const spoolWidth = fieldsValues?.spool?.assetsValue?.data?.widthInCentimeter;
  const spoolMaterialId = fieldsValues?.spool?.assetsValue?.data?.material?.id;
  const hasSpoolMismatch =
    (spoolWidth !== undefined && firstCassetteWidth !== undefined && spoolWidth !== firstCassetteWidth) ||
    (spoolMaterialId !== undefined && firstCassetteMaterialId !== undefined && spoolMaterialId !== firstCassetteMaterialId);

  const cassetteLengthsTotal = fieldsValues?.cassettesList?.reduce((sum, item) => sum + (+(item?.length ?? 0)), 0) ?? 0;
  const spoolLengthLeft = fieldsValues?.spool?.assetsValue?.data?.lengthLeft ?? 0;

  useEffect(() => {
    let initialValues = {
      cassettesList: getInitialCassettes(sData?.get('preselectedAssets')?.toJS()) ?? [{}],
    };

    initialize(initialValues);
  }, []);

  useEffect(() => {
    const material = fieldsValues?.cassettesList?.[0]?.asset?.assetsValue?.data?.material;

    setSpoolingInfo([
      {
        label: labels.get('material'),
        value: material ?  MaterialHelper.getMaterialFullLabel(material?.materialName, material?.businessId)
          : labels.get('notavailable'),
      },
      {
        label: labels.get('width'),
        value: firstCassetteWidth !== undefined
          ? UnitHelper.serverValueToUserValueWithLabel(unitTypes.WIDTH, firstCassetteWidth)
          : labels.get('notavailable'),
      },
    ]);
  }, [firstCassetteWidth, firstCassetteMaterialId, labels]);

  useEffect(() => {
    if (hasSpoolMismatch) {
      touch('spool.assetsValue');
    }
  }, [hasSpoolMismatch]);

  useEffect(() => {
    updateTotalLength();
  }, [cassetteLengthsTotal, spoolLengthLeft]);


  const getDialogButtons = () => {
    return {
      left: [
        {
          id: 'cancel',
          text: dialogLabels.get('cancel'),
          action: actions.hide,
        },
      ],
      right: [
        {
          id: 'submit',
          text: labels.get('footer.performOperation'),
          bsStyle: 'primary',
          loading: sData.get('loading'),
          action: handleSubmit(onSubmit),
          disabled: pristine || sData.get('loading') ||
                    sData.get('hasError') ||
                    (sData.get('showIgnoreValidationCheckbox') &&
                      !sData.get('isIgnoreValidationWarnings')),
        },
      ],
    };
  }

  const onSubmit = (data) => {

    const spoolBusinessId = isNewSpool ? data?.spool?.assetsValue?.value : data?.spool?.assetsValue?.data?.businessId
    let submitData = {
      workOrder: data?.workOrder?.assetsValue?.label ?? null,
      sourceCassettes: createSourceCassettes(data.cassettesList ?? []),
      businessId: spoolBusinessId,
      spoolStatus: data?.spoolStatus?.value,
      comment: data?.comment ?? '',
      ignoreValidationWarnings:
        sData.get('showIgnoreValidationCheckbox') && data.footerValidationCheckbox
          ? data.footerValidationCheckbox
          : false,
    };

    actions.submit(submitData, null, reloadParentComponent, printPdfLabels);
  };

  const onHide = () => {
    actions.hide();
  };

  const updateTotalLength = () => {
    const totalLength = round(cassetteLengthsTotal + UnitHelper.serverValueToUserValue(unitTypes.LENGTH, spoolLengthLeft), UOM_DEFAULT_PRECISION);
    change('length', totalLength);
  }

  const getCassettesItemRenderer = (data) => {
    return (
      <AssetLengthListItem
        {...data}
        fetchConfig={cassetteFetchConfig}
        change={change}
        touch={touch}
        assetType={getEnumValue(enumTypes.OBJECT_TYPE)('CASSETTE')}
      />
    )
  }

  const generateSpoolId = () => {
    actions.generateSpoolId().then((response) => {
      if (!response?.data) {
        return;
      }
      const newOption = {
        value: response.data,
        label: response.data,
        data: {
          id: response.data,
          objectType: getEnumValue(enumTypes.OBJECT_TYPE)('SPOOL'),
        },
      };
      change('spool.assetsValue', newOption);
      setIsNewSpool(true);
    });
  };

  const onSpoolChangeCallback = (newValue, oldValue) => {
    if ((newValue && oldValue && newValue.value === oldValue.value) || newValue === null) {
      return;
    }

    setIsNewSpool(newValue.label === newValue.value);
  }

  const getFooterValidationCheckBox = () => {
    return sData.get('showIgnoreValidationCheckbox')
      ? { label: dialogLabels.get('ignorewarnings'), onChange: onFooterValidationChange }
      : null;
  };

  const onFooterValidationChange = (value) => {
    actions.toggleIgnoreValidationWarningsCheckbox(value);
  };

  const widthValidation = useCallback((value) => {
    const width = value?.data?.widthInCentimeter;

    if (width !== undefined && firstCassetteWidth !== undefined && width !== firstCassetteWidth) {
      return validationLabels.get('incorrectWidth');
    }

    return undefined;
  }, [firstCassetteWidth]);

  const materialValidation = useCallback((value) => {
    const materialId = value?.data?.material?.id;

    if (materialId !== undefined && firstCassetteMaterialId !== undefined && materialId !== firstCassetteMaterialId) {
      return validationLabels.get('incorrectMaterial');
    }

    return undefined;
  }, [firstCassetteMaterialId]);

  const validate = useMemo(() => [Validation.required, maxLength100, materialValidation, widthValidation,], [ widthValidation, materialValidation]);

  const widthValueRenderer  = (value) => {
    if (value && value.label === value.value) {
      return null;
    }
    const assetWidth = UnitHelper.serverValueToUserValueWithLabel(unitTypes.WIDTH, value?.data?.widthInCentimeter || 0);
    return (
      <Label className='column custom-data' text={assetWidth}  />);
  };

  const getIconPopOver = () => {
    return (<AlertPopover popoverType={AlertPopoverType.HINT}
                          popoverComponent={<div>{labels.get('length.hint')}</div>}
    />);
  };

  return (
    <Dialog
      id="create-spool-from-cassettes-dialog"
      className="create-spool-from-cassettes-dialog"
      titleText={labels.get('header.title')}
      footerCheckbox={footerCheckbox}
      show={sData.get('show')}
      onHide={onHide}
      sData={sData}
      footerButtons={getDialogButtons()}
      footerValidationCheckbox={getFooterValidationCheckBox()}
      footerInformationIcon={dialogHelper.getFooterInformationIcon(
        sData,
        dialogLabels
      )}
      onEnterKeyPress={handleSubmit(onSubmit)}
    >
      <InputSection label={labels.get('workOrder')} htmlFor="workOrder"
                    className="work-order-section inline left-side no-margin">
        <EntitiesMultiSelect
          id="workOrder"
          name="workOrder"
          entitiesTypes={WO_ENTITIES_TYPES}
          fetchConfig={woFetchConfig}
          change={change}
          isMulti={false}
          showEntitiesTypes={false}
          autoFocus={true}
        />
      </InputSection>

      <InputSection className='inline right-side'/>

      <div className='spooling-info-section input-section inline left-side'>
        <NameValuePairs items={spoolingInfo}/>
      </div>

      <div className='assets-container'>

        <div className='assets-container-left'>
          <div className="list-title">
            <span>{labels.get('cassettes') + '*'}</span>
            <div>
              <span>{labels.get('length')}</span>
              <span className="units">{`(${UnitHelper.getLabelForUnitType(unitTypes.LENGTH)})*`}</span>
            </div>
          </div>

          <List
            id='cassettesList'
            name='cassettesList'
            className='cassettes-list'
            itemRenderer={getCassettesItemRenderer}
            minFieldsCount={1}
            maxItemsToOverflow={6}
            addButtonLabel={labels.get('cassettesList.button.add')}
            showAddButton={!hasEmptyItems(fieldsValues?.cassettesList)}
          />
        </div>

        <span className="add-to-asset-arrow">
          <i className="pl pl-move-arrow-right"></i>
        </span>

        <div className='assets-container-right'>
          <div className="list-title">
            <span>{labels.get('spool') + '*'}</span>
            <div className="spool-length-title">
              <span>{labels.get('length')}</span>
              <span className="units">{`(${UnitHelper.getLabelForUnitType(unitTypes.LENGTH)})*`}</span>
              {getIconPopOver()}
            </div>
          </div>

          <div className='spool-section'>
            <EntitiesMultiSelectWithChildren
              id="spool"
              name="spool"
              entitiesTypes={[getEnumValue(enumTypes.OBJECT_TYPE)('SPOOL')]}
              showEntitiesTypes={false}
              validate={validate}
              fetchConfig={spoolFetchConfig}
              isMulti={false}
              allowNewOption={true}
              change={change}
              onValueChangeCallback={onSpoolChangeCallback}
              customValueRenderer={widthValueRenderer}
              customOptionRenderer={widthValueRenderer}
            >
              <div className="input-with-generate-button">
                <Button id={'-button'}
                        className={' generateButton border-btn btn'}
                        onClick={generateSpoolId}
                >
                  {dialogLabels.get('generate')}
                </Button>
              </div>
            </EntitiesMultiSelectWithChildren>

            <TextField id='length'
                       name='length'
                       className="spool-length"
                       disabled={true}
            />
          </div>

          <InputSection label={labels.get('spoolStatus')} htmlFor="spoolStatus" className="">
            <Combobox
              id="spoolStatus"
              name="spoolStatus"
              options={getStatuses()}
              optionRenderer={getComponentToRender}
            />
          </InputSection>
        </div>

      </div>

      <InputSection label={labels.get('comment')} htmlFor="comment" className="full-width">
        <TextAreaField id="comment-textField" name="comment" className="comment-textField"
                       placeholder={labels.get('comment.placeholder')}/>
      </InputSection>

    </Dialog>
  );
}


CreateSpoolFromCassettesDialog = reduxForm({
  form: 'createSpoolFromCassettesDialog',
  onChange: (values, dispatch, props, previousValues) => {
    dialogHelper.onFormChangeHandler(values, props, previousValues);
  },
})(CreateSpoolFromCassettesDialog);

const selector = formValueSelector('createSpoolFromCassettesDialog');

export default CreateSpoolFromCassettesDialog = connect( state => {
  return {  fieldsValues: selector(state, 'cassettesList', 'spool') }
})( CreateSpoolFromCassettesDialog );


CreateSpoolFromCassettesDialog.propTypes = {
  actions: PropTypes.object.isRequired,
  fetchEntitiesActions: PropTypes.object.isRequired,
  sData: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func,
  change: PropTypes.func,
  touch: PropTypes.func,
  initialize: PropTypes.func,
  pristine: PropTypes.bool,
  fieldsValues : PropTypes.object,
  reloadParentComponent: PropTypes.func,
};
